export interface Payload {
  $schema: string;
  questionnaire: Questionnaire;
}

export interface Translation {
  id?: string;
  [locale: string]: string;
}

export interface Questionnaire {
  id: string;
  status: string;
  revision: number;
  softwareVersion: string;
  tenantId: string|number;
  meta: Questionnaire.Meta;
  createdAt?: string;
  updatedAt?: string;
  questions: Questionnaire.Container[];
  accumulated?: Questionnaire.Accumulated;
}

export namespace Questionnaire {
  export interface Accumulated {
    index: Questionnaire.Accumulated.QuestionnaireIndex;
    pathHashes: Questionnaire.Accumulated.QuestionnairePathHashMap;
    questionPathHashes: Questionnaire.Accumulated.QuestionnaireQuestionPathHashMap;
    conditionPathHashes: Questionnaire.Accumulated.ConditionToContainerPathHashMap;
    initialNumberOfMandatoryQuestions: number;
    initialNumberOfQuestions: number;
  }

  export namespace Accumulated {
    export interface QuestionnairePathHashMap {
      [pathHash: string]: Questionnaire.Container;
    }

    export interface QuestionnaireQuestionPathHashMap {
      [pathHash: string]: {
        isOnPageNumber: number;
        isMandatory: boolean;
      };
    }

    export interface ConditionToContainerPathHashMap {
      [pathHash: string]: string[];
    }

    export interface QuestionnaireIndex {
      pages: QuestionnaireIndex.Page[];
    }

    export namespace QuestionnaireIndex {
      export interface Page {
        title: Translation;
        pageNumber: number;
        questions: Question[];
        expanded?: boolean;
      }

      export interface Question {
        pathHash: string;
        title: Translation;
        visible: boolean;
      }
    }
  }

  export interface Meta {
    author: string;
    title: Translation;
    type: string;
    description: Translation;
    image?: Translation;
    asset?: {
      image?: Container.Asset,
      video?: Container.Asset,
      audio?: Container.Asset
    };
    options?: {
      [prop: string]: any,
      pdf?: {
        skipUnsigned?: boolean
        generateInternal?: boolean
        forms?: string[]
      },
      gdt?: {
        addFormulaResults?: boolean
        addQuestionnaireTitle?: boolean
        addScoring?: boolean
      },
      fill?: {
        forceMandatory?: boolean
        skipList?: boolean
      },
      mail?: {
        filler: {
          internalPdf?: boolean,
          filledPdf?: boolean
        },
        user: {
          internalPdf?: boolean,
          filledPdf?: boolean
        }
      },
      excel?: {
        omitPatientData?: false
      },
      variables?: Options.Variables;
      variablesMapping?: Options.VariablesMapping[];
    };
    tags?: string[];
  }

  export interface Conditions {
    show?: string | string[];
    mark?: string;
    expression?: string;
  }

  export interface Description {
    type: string;
    text: Translation;
  }

  export namespace Options {
    export interface Variables {
      [scope: string]: Variables.Scope;
    }

    export interface VariablesMapping {
      source: {
        scope: string,
        variable: string
      };
      target: {
        scope: string,
        variable: string
      };
    }

    export namespace Variables {
      export interface Scope {
        [variableName: string]: {
          [pathHash: string]: any;
        };
      }

      export namespace Scope {
        export interface Flipped {
          [pathHash: string]: string;
        }
      }
    }

    export interface NetDiagramData {
      title: Translation;
      entries: NetDiagramEntry[];
    }

    export interface NetDiagramEntry {
      label: Translation;
      variable?: string;
      value?: number;
    }

    export interface ChartEntry {
      label: Translation;
      variable: string;
      color: string;
    }

    export interface LineChartLine {
      id: string;
      title: Translation;
      color: string;
    }

    export interface LineChartEntry extends ChartEntry {
      lineId: string;
    }

    export interface BarChartData {
      title: Translation;
      widthPercent: number;
      alignment: string;
      xAxisLabel: Translation;
      yAxisLabel: Translation;
      entries: ChartEntry[];
    }

    export interface ColumnChartData {
      title: Translation;
      widthPercent: number;
      alignment: string;
      xAxisLabel: Translation;
      yAxisLabel: Translation;
      entries: ChartEntry[];
    }

    export interface LineChartData {
      title: Translation;
      widthPercent: number;
      alignment: string;
      xAxisLabel: Translation;
      yAxisLabel: Translation;
      lines: LineChartLine[];
      entries: LineChartEntry[];
    }

    export interface PieChartData {
      title: Translation;
      widthPercent: number;
      alignment: string;
      sortByValue: boolean;
      entries: ChartEntry[];
    }
  }

  export interface Container {
    id: string;
    packedId?: string;
    pathHash?: string;
    parentHash?: string;
    path?: string;
    title?: Translation;
    appendix?: Translation;
    description?: Description;
    format?: string;
    extra?: string;
    undeletable?: boolean;
    dataType?: string;
    displayType?: string;
    omitContainer?: boolean;
    assets?: Container.Assets;
    options?: Container.Options;
    elements?: Container[];
    conditions?: Conditions;
  }

  export namespace Container {
    export interface Options {
      active?: boolean;
      mandatory?: boolean;
      min?: number;
      max?: number;
      mark?: boolean;
      hideInPdf?: boolean;
      hideInApp?: boolean;
      hideInXls?: boolean;
      steps?: number;
      unit?: Translation;
      disableWheelPicker?: boolean;
      fullDateTime?: boolean;
      fullDate?: boolean;
      monthDate?: boolean;
      yearDate?: boolean;
      multipleAnswers?: boolean;
      invertAxes?: boolean;
      drawColor?: string;
      bgColor?: string;
      bgImage?: Translation;
      height?: number;
      autoUnlock?: boolean;
      drawScale?: boolean;
      allowUpload?: boolean;
      repeatable?: boolean;
      minRepeat?: number;
      maxRepeat?: number;
      widthPercent?: number;
      validation?: Validation;
      netDiagram?: Options.NetDiagramData[];
      barChart?: Options.BarChartData;
      columnChart?: Options.ColumnChartData;
      lineChart?: Options.LineChartData;
      pieChart?: Options.PieChartData;
      multiline?: boolean;
      html?: boolean;
      scanOptions?: ScanOptions;
      email?: {
        recipientsFixed?: string[];
        sendToAssigner?: boolean;
        sendToFiller?: boolean;
        attachFilledPdf?: boolean;
        attachReportPdf?: boolean;
        attachFilledDocx?: boolean;
        attachReportDocx?: boolean;
        includeAssetLinks?: boolean;
      };
    }

    export interface Validation {
      regex?: string;
      mod?: string;
      hint?: Translation;
    }

    export interface ScanOptions {
      autoDetectEdges?: boolean;
      allowMultiplePages?: boolean;
      minPages?: number;
      maxPages?: number;
      outputFormat?: 'pdf' | 'jpg' | 'png';
      imageQuality?: 'low' | 'medium' | 'high';
      allowFilters?: boolean;
      defaultFilter?: 'none' | 'blackwhite' | 'grayscale' | 'color';
      allowRetake?: boolean;
      showPreview?: boolean;
    }

    export interface Asset {
      filename: Translation;
      title: Translation;
      options?: Asset.Options;
    }

    export interface Assets {
      videos?: Asset[];
      images?: Asset[];
      audio?: Asset[];
    }

    export namespace Asset {
      export interface Options {
        croppedImage?: Translation;
        cropperPosition?: {x1: number, y1: number, x2: number, y2: number};
        previewImage?: Translation;
        allowFullscreen?: boolean;
        allowControls?: boolean;
        autoPlay?: boolean;
      }
    }

    export class DisplayType {
      static GROUP = 'group';
      static TEXT = 'text';
      static RADIO = 'radio';
      static CHECKBOX = 'checkbox';
      static PDF = 'pdf';
    }

    export class DataType {
      STRING: 'string';
      BOOLEAN: 'boolean';
      NUMBER: 'number';
      DATE: 'date';
    }

    export class Format {
      static PAGE_BREAK = 'pageBreak';
      static SINGLE_CHOICE = 'singleChoice';
      static MULTIPLE_CHOICE = 'multipleChoice';
      static MATRIX = 'matrix';
      static NUMERIC = 'numeric';
      static TEXT = 'text';
      static DATE = 'date';
      static INFO = 'info';
      static CANVAS = 'canvas';
      static PICTURE = 'picture';
      static SIGNATURE = 'signature';
      static NET_DIAGRAM = 'netDiagram';
      static BAR_CHART = 'barChart';
      static COLUMN_CHART = 'columnChart';
      static LINE_CHART = 'lineChart';
      static PIE_CHART = 'pieChart';
      static DOCUMENT_SCAN = 'documentScan';
      static MULTI_VALUE = 'multiValue';

      static YES_NO_MISC = 'singleChoice#yesNoMisc';
      static MULTIPLE_CHOICE_MISC = 'multipleChoice#multipleChoiceMisc';
      static YES_NO = 'singleChoice#yesNo';
      static SCALE = 'matrix#scale';
      static TEXT_MULTILINE = 'text#multiline';
    }
  }
}
export interface Submission {
  _id: string;
  id?: string;
  uid?: string;
  revision?: number;
  swVersion?: string;
  bucketId?: string;
  requestId?: string;
  clientId?: string;
  surveyId?: string;
  client: any;
  questionnaire: Questionnaire;
  answers: {[answerPatHash: string]: any};
  variables: {
    scoring: {[varName: string]: number}
    inline: {[varName: string]: string | boolean | number};
    container: {[varName: string]: string | boolean | number};
  };
  visibilities: {[patHash: string]: boolean};
  signDocuments: {
    [documentId: string]: {
      documentId: string,
      documentName: string,
      sourceFileName: string,
      targetFileName: string,
      sourceType: string,
      targetType: string,
      processed: boolean
    }
  };
  uploadLinks?: any;
  exports?: any;
  error?: {
    message: string,
    stack: string
  };
  status?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Client {
  uid?: string;
  customerNr?: string | number;
  caseNr?: string | number;
  gender?: string;
  lastName?: string;
  firstName?: string;
  address?: string;
  status?: string;
  birthDate?: string;
  location?: string;
  createdAt?: string;
  updatedAt?: string;
  anonymous?: boolean;
  pseudonymous?: boolean;
  pseudoId?: string;
  questionnaires?: Questionnaire[];
  custom?: {[key: string]: any};
}

export namespace Workflow {
  export interface Stage {
    name?: Translation;
    description?: Translation;
    questionnaires: string[];
    expression?: string;
    autoStart?: boolean;
    skipSigning?: boolean;
    generateExports: boolean;
    generateFormPDFs: boolean;
    prefill?: boolean;
  }
}

export interface Workflow {
  _id: string;
  id?: string;
  uid?: string;
  name: Translation;
  description?: Translation;
  stages?: Workflow.Stage[];
  updatedAt?: Date;
  createdAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export namespace Survey {
  export enum Status {
    created = 'created',
    assigned = 'assigned',
    submitted = 'submitted',
    signed = 'signed',
    unknown = 'unknown'
  }
}

export namespace WorkflowInstance {
  export enum Status {
    created = 'created',
    assigned = 'assigned',
    submitted = 'submitted',
    canceled = 'canceled',
  }
}

export interface WorkflowInstance {
  _id: string;
  id?: string;
  uid?: string;
  clientId: string;
  client?: Client;
  locale?: string;
  validUntil?: Date;
  workflowId?: string;
  workflow?: Workflow;
  surveys?: Survey[];
  status: WorkflowInstance.Status;
  stage?: number;
  markedDone?: boolean;
  surveyId: string;
  updatedAt?: Date;
  createdAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface Survey {
  _id: string;
  id?: string;
  uid?: string;
  clientId: string;
  appDataId?: string;
  appDataLink?: string;
  locale?: string;
  client: Client;
  questionnaires: Questionnaire[];
  submissions: Submission[];
  additionalData?: any;
  notificationApiUri?: string;
  returnUri?: string;
  validUntil?: Date;
  signing: boolean;
  allowMail?: boolean;
  status: Survey.Status;
  createdBy?: string;
  updatedAt?: string;
  createdAt?: string;
}
